/**
 * Lobby overlay: room code, game picker, team size, side selection, start.
 *
 * One view for both ends. The host's controls mutate the lobby directly; a
 * guest's controls send intents and wait for the host's next broadcast, so the
 * screen always shows what the host believes rather than a local guess.
 */
import { type LobbyState, TEAM_SIZES, countSide } from '@office/net';
import type { Side } from '@office/sim';
import { GAMES, findGame } from './games.js';
import { TEAM_NAMES, teamColor, tintFor } from '../games/volley/colors.js';

const STYLE = `
.lobby {
  position: fixed; inset: 0; z-index: 40;
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  background: var(--bg, #0b0f18);
  font: 14px/1.5 var(--sans, system-ui, sans-serif);
  color: var(--fg, #e6ebf5);
}
.lobby[hidden] { display: none !important; }
.lobby .card {
  width: min(540px, 100%);
  max-height: 100%;
  overflow-y: auto;
  padding: 4px 2px;
}
.lobby h2 { margin: 0; font-size: 13px; font-weight: 500; color: var(--muted, #7f95b8); }
.lobby .code {
  font: 700 40px/1.1 var(--mono, monospace);
  letter-spacing: 0.28em; color: var(--fg, #e6ebf5); margin: 2px 0 4px;
}
.lobby .muted { color: var(--muted, #7f95b8); font-size: 13px; margin: 0; }
.lobby section { margin-top: 20px; }
.lobby h3 {
  margin: 0 0 8px; font: 500 12px/1 var(--sans, system-ui, sans-serif);
  color: var(--muted, #7f95b8);
}
.lobby button {
  border: 0; border-radius: var(--radius, 12px);
  background: var(--surface, rgba(255,255,255,0.06)); color: inherit;
  font: 500 14px var(--sans, system-ui, sans-serif); cursor: pointer;
  transition: background 80ms ease;
}
.lobby button:hover:not(:disabled) { background: var(--surface-hi, rgba(255,255,255,0.12)); }
.lobby button:focus-visible { outline: 2px solid var(--accent, #4fd1c5); outline-offset: 2px; }
.lobby button:disabled { opacity: 0.45; cursor: default; }
.lobby .games { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.lobby .game { padding: 10px 12px; text-align: left; }
.lobby .game strong { display: block; font-size: 13px; font-weight: 600; }
.lobby .game span { color: var(--muted, #7f95b8); font-size: 11px; }
.lobby .game[aria-pressed="true"] { box-shadow: inset 0 0 0 2px var(--accent, #4fd1c5); }
.lobby .sizes { display: flex; gap: 8px; }
.lobby .sizes button { flex: 1; padding: 9px; }
.lobby .sizes button[aria-pressed="true"] { box-shadow: inset 0 0 0 2px var(--accent, #4fd1c5); }
.lobby .teams { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.lobby .team {
  padding: 10px; border-radius: var(--radius, 12px);
  background: var(--surface, rgba(255,255,255,0.06)); min-height: 108px;
}
.lobby .team header { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 12px; }
.lobby .team header span { color: var(--muted, #7f95b8); }
.lobby .team.teal header strong { color: #4fd1c5; }
.lobby .team.pink header strong { color: #f472b6; }
.lobby .team ul { list-style: none; margin: 0 0 8px; padding: 0; display: grid; gap: 4px; }
.lobby .team li {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 8px; border-radius: 8px; background: rgba(0,0,0,0.25); font-size: 13px;
}
.lobby .team li .dot { width: 10px; height: 10px; border-radius: 50%; flex: none; }
.lobby .team li.you { box-shadow: inset 0 0 0 1px rgba(255,255,255,0.3); }
.lobby .team li .tag { margin-left: auto; color: var(--muted, #7f95b8); font-size: 11px; }
.lobby .team .switch {
  width: 100%; padding: 7px; background: transparent; color: var(--muted, #7f95b8); font-size: 12px;
}
.lobby .team .switch:hover:not(:disabled) { color: var(--fg, #e6ebf5); }
.lobby .row { display: flex; gap: 8px; align-items: center; margin-top: 20px; }
.lobby input[type=text] {
  flex: 1; min-width: 0; padding: 10px 12px; border: 0; border-radius: var(--radius, 12px);
  background: var(--surface, rgba(255,255,255,0.06)); color: inherit; font: inherit;
}
.lobby input[type=text]:focus { outline: 2px solid var(--accent, #4fd1c5); }
.lobby .go {
  padding: 11px 18px; background: var(--accent, #4fd1c5); color: var(--accent-ink, #062b28); font-weight: 600;
}
.lobby .go:hover:not(:disabled) { background: #6ee0d5; }
.lobby .ghost { padding: 9px 14px; color: var(--muted, #7f95b8); }
.lobby .ghost:hover { color: var(--fg, #e6ebf5); }
.lobby .waiting { margin-top: 12px; color: var(--muted, #7f95b8); font-size: 13px; }
`;

export interface LobbyController {
  isHost: boolean;
  selfId: string;
  /** Null until the first lobby broadcast lands (guest side). */
  state(): LobbyState | null;
  setName(name: string): void;
  setSide(side: Side): void;
  setTeamSize(size: number): void;
  setGame(id: string): void;
  start(): void;
  /** Shareable link for the room. */
  inviteUrl(): string;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Partial<HTMLElementTagNameMap[K]> & { dataset?: Record<string, string> } = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  const { dataset, ...rest } = props;
  Object.assign(node, rest);
  if (dataset) Object.assign(node.dataset, dataset);
  node.append(...children);
  return node;
}

export class LobbyView {
  readonly element: HTMLElement;
  private readonly card: HTMLElement;
  private nameValue = '';

  constructor(private readonly controller: LobbyController) {
    if (!document.getElementById('lobby-style')) {
      const style = el('style', { id: 'lobby-style', textContent: STYLE });
      document.head.append(style);
    }
    this.card = el('div', { className: 'card' });
    this.element = el('div', { className: 'lobby' }, this.card);
    document.body.append(this.element);
    this.render();
  }

  show(): void {
    this.element.hidden = false;
    this.render();
  }

  hide(): void {
    this.element.hidden = true;
  }

  destroy(): void {
    this.element.remove();
  }

  render(): void {
    const state = this.controller.state();
    this.card.replaceChildren();

    if (!state) {
      this.card.append(
        el('h2', {}, 'Joining…'),
        el('p', { className: 'muted' }, 'Waiting for the host to answer.'),
      );
      return;
    }

    const self = state.players.find((p) => p.id === this.controller.selfId);
    this.nameValue = self?.name ?? this.nameValue;

    this.card.append(
      el('h2', {}, this.controller.isHost ? 'Your room' : 'Room'),
      el('div', { className: 'code' }, state.room),
      el(
        'p',
        { className: 'muted' },
        this.controller.isHost
          ? 'Read the code out, or send the link.'
          : 'Waiting for the host to start.',
      ),
    );

    if (this.controller.isHost) {
      const copy = el('button', { className: 'ghost', type: 'button' }, 'Copy invite link');
      copy.addEventListener('click', () => {
        void navigator.clipboard
          .writeText(this.controller.inviteUrl())
          .then(() => {
            copy.textContent = 'Link copied';
            window.setTimeout(() => (copy.textContent = 'Copy invite link'), 1800);
          })
          .catch(() => (copy.textContent = this.controller.inviteUrl()));
      });
      this.card.append(el('div', { className: 'row' }, copy));
    }

    this.card.append(this.gameSection(state), this.sizeSection(state), this.teamSection(state));

    const name = el('input', {
      type: 'text',
      value: this.nameValue,
      placeholder: 'Your name',
      maxLength: 16,
    });
    name.addEventListener('change', () => this.controller.setName(name.value));

    if (this.controller.isHost) {
      const go = el('button', { className: 'go', type: 'button' }, 'Start match');
      const ready = countSide(state, 0) >= 1 && countSide(state, 1) >= 1;
      go.disabled = !ready;
      go.addEventListener('click', () => this.controller.start());
      this.card.append(el('div', { className: 'row' }, name, go));
      if (!ready) {
        this.card.append(
          el('p', { className: 'waiting' }, 'Both sides need at least one player.'),
        );
      }
    } else {
      this.card.append(el('div', { className: 'row' }, name));
      this.card.append(
        el('p', { className: 'waiting' }, 'Pick a side. The host starts the match.'),
      );
    }
  }

  private gameSection(state: LobbyState): HTMLElement {
    const grid = el('div', { className: 'games' });
    for (const game of GAMES) {
      const button = el(
        'button',
        { className: 'game', type: 'button' },
        el('strong', {}, game.title),
        el('span', {}, game.tagline),
      );
      button.setAttribute('aria-pressed', String(game.id === state.game));
      button.disabled = !game.available || !this.controller.isHost;
      button.addEventListener('click', () => this.controller.setGame(game.id));
      grid.append(button);
    }
    return el('section', {}, el('h3', {}, 'Game'), grid);
  }

  private sizeSection(state: LobbyState): HTMLElement {
    const row = el('div', { className: 'sizes' });
    const game = findGame(state.game);
    for (const size of TEAM_SIZES) {
      const button = el('button', { type: 'button' }, `${size}v${size}`);
      button.setAttribute('aria-pressed', String(size === state.teamSize));
      // A size that cannot hold everyone already in the room is not offered.
      button.disabled =
        !this.controller.isHost ||
        !game.teamSizes.includes(size) ||
        state.players.length > size * 2;
      button.addEventListener('click', () => this.controller.setTeamSize(size));
      row.append(button);
    }
    return el('section', {}, el('h3', {}, 'Teams'), row);
  }

  private teamSection(state: LobbyState): HTMLElement {
    const teams = el('div', { className: 'teams' });
    const labels: Record<number, string> = { 0: TEAM_NAMES[0], 1: TEAM_NAMES[1] };

    for (const side of [0, 1] as Side[]) {
      const members = state.players.filter((p) => p.side === side);
      const list = el('ul', {});
      members.forEach((player, index) => {
        const dot = el('span', { className: 'dot' });
        dot.style.background = tintFor(side, index);
        const li = el(
          'li',
          { className: player.id === this.controller.selfId ? 'you' : '' },
          dot,
          player.name,
        );
        if (player.host) li.append(el('span', { className: 'tag' }, 'host'));
        else if (player.id === this.controller.selfId) li.append(el('span', { className: 'tag' }, 'you'));
        list.append(li);
      });
      for (let i = members.length; i < state.teamSize; i++) {
        const slot = el('li', {}, 'empty');
        slot.style.opacity = '0.35';
        list.append(slot);
      }

      const self = state.players.find((p) => p.id === this.controller.selfId);
      const switchBtn = el('button', { className: 'switch', type: 'button' }, `Join ${labels[side]}`);
      switchBtn.disabled = self?.side === side || members.length >= state.teamSize;
      switchBtn.addEventListener('click', () => this.controller.setSide(side));

      teams.append(
        el(
          'div',
          { className: `team ${side === 0 ? 'teal' : 'pink'}` },
          el(
            'header',
            {},
            el('strong', {}, labels[side] ?? ''),
            el('span', {}, `${members.length}/${state.teamSize}`),
          ),
          list,
          switchBtn,
        ),
      );
    }
    return el('section', {}, el('h3', {}, 'Sides'), teams);
  }
}
